import type { RoiId } from '~/domain/changes/ids';
import { Image3D } from '~/domain/patient/image3d';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { StructureSetObject } from '~/engine/data/structure/StructureSetObject';
import { DrawingConstraints } from '~/engine/tool/actions/DrawActionState';
import {
    displayRangeToRaw,
    isThresholdSupportedModality,
} from '~/engine/tool/actions/contouring-actions/intensityThreshold';
import {
    BoundingRoiConstraint,
    CollisionConstraint,
    IntensityThresholdConstraint,
} from '~/state/atoms/toolsAtoms';

/**
 * Why an enabled intensity-threshold constraint could not be applied. `null`
 * means it resolved (or was not enabled). The caller surfaces this so an
 * enabled threshold never silently draws unconstrained.
 */
export type IntensityThresholdUnresolved =
    | null
    | 'no-image'
    | 'unsupported-modality'
    | 'suv-unavailable'
    | 'degenerate-range';

export interface ResolvedDrawingConstraints {
    /** The constraints to pass to the contouring tool. */
    constraints: DrawingConstraints;
    /**
     * The structure of an enabled Inside constraint that could not be resolved
     * in the active structure set (deleted, or in another set), or null. The constraint
     * is then inactive — the caller must surface this rather than silently
     * drawing unconstrained.
     */
    boundingUnresolved: RoiId | null;
    /** Count of enabled Outside structures that could not be resolved. */
    protectedUnresolved: number;
    /**
     * Why an enabled Threshold constraint is inactive, or null when it resolved
     * / was not enabled (see {@link IntensityThresholdUnresolved}).
     */
    intensityUnresolved: IntensityThresholdUnresolved;
}

/**
 * Resolve the UI-state drawing constraints (the Recoil atoms) against the
 * active structure set / image into engine {@link DrawingConstraints} for the
 * tools.
 *
 * Only enabled constraints resolve. An enabled Inside/Outside constraint
 * naming a structure the active structure set does not hold is reported as
 * unresolved so the caller can make the
 * inactive state visible. The Threshold constraint is resolved against the
 * active image: its display-unit range is converted to the image's raw scale
 * (HU passes through; SUVbw is divided by the image's suvFactor). When the
 * modality is unsupported or PET SUV metadata is missing, the threshold is
 * reported unresolved rather than applied in the wrong units.
 */
export function resolveDrawingConstraints(
    bounding: BoundingRoiConstraint,
    collision: CollisionConstraint,
    threshold: IntensityThresholdConstraint,
    structureSet: StructureSetObject | undefined,
    image: Image3D | undefined
): ResolvedDrawingConstraints {
    let boundingRoi: RegionOfInterest | undefined;
    let boundingUnresolved: RoiId | null = null;
    if (bounding.enabled && bounding.roiId !== null) {
        boundingRoi = structureSet?.getRoi(bounding.roiId);
        if (!boundingRoi) {
            boundingUnresolved = bounding.roiId;
        }
    }

    // The active Inside bound must never also be subtracted as an Outside
    // protection: the clip AND-s the stroke with the bound then AND_NOT-s the
    // protected union, so the same SDF on both sides erases the whole stroke.
    // Drop it here as well as at clip time so the invariant does not depend on
    // the UI pruning effect alone.
    const activeBoundingRoiId =
        bounding.enabled && bounding.roiId !== null ? bounding.roiId : null;

    let protectedStructures: RegionOfInterest[] | undefined;
    let protectedUnresolved = 0;
    if (collision.enabled && collision.roiIds.length > 0) {
        const resolved: RegionOfInterest[] = [];
        for (const roiId of collision.roiIds) {
            if (roiId === activeBoundingRoiId) {
                // Intentionally excluded (safety), not an unresolved failure —
                // do not count it toward protectedUnresolved.
                continue;
            }
            const roi = structureSet?.getRoi(roiId);
            if (roi) {
                resolved.push(roi);
            } else {
                protectedUnresolved++;
            }
        }
        protectedStructures = resolved.length > 0 ? resolved : undefined;
    }

    let intensityThreshold: DrawingConstraints['intensityThreshold'];
    let intensityUnresolved: IntensityThresholdUnresolved = null;
    // Only apply an enabled threshold once it holds a real band (`seeded`) — the
    // unseeded `{0, 0}` placeholder (e.g. before the UI has seeded it, or on a
    // degenerate image that never seeds) must never be converted and AND-ed in,
    // which would wipe the stroke.
    if (threshold.enabled && threshold.seeded) {
        const modality = image?.firstSlice.modality;
        if (!image) {
            intensityUnresolved = 'no-image';
        } else if (!isThresholdSupportedModality(modality)) {
            intensityUnresolved = 'unsupported-modality';
        } else if (!(image.minValue < image.maxValue)) {
            // Uniform image — no intensity range to threshold over. Surface it
            // rather than AND-ing a band that would deposit ~nothing.
            intensityUnresolved = 'degenerate-range';
        } else {
            const raw = displayRangeToRaw(
                { lower: threshold.lower, upper: threshold.upper },
                modality,
                image.suvFactor
            );
            if (!raw) {
                // Supported modality but range not expressible — for PET this
                // means the series lacks (usable) metadata to compute a SUV factor.
                intensityUnresolved = 'suv-unavailable';
            } else {
                intensityThreshold = raw;
            }
        }
    }

    return {
        constraints: {
            bounding: boundingRoi,
            protectedStructures,
            intensityThreshold,
        },
        boundingUnresolved,
        protectedUnresolved,
        intensityUnresolved,
    };
}

/**
 * The constraints to arm a contouring tool with. An enabled constraint that
 * cannot be resolved is reported to the console and left inactive, so a
 * stroke is never silently unconstrained while the control shows the
 * constraint as on. The report names structure ids and reasons only, never
 * patient data.
 */
export function resolveArmingConstraints(
    bounding: BoundingRoiConstraint,
    collision: CollisionConstraint,
    threshold: IntensityThresholdConstraint,
    structureSet: StructureSetObject | undefined,
    image: Image3D | undefined
): DrawingConstraints {
    const {
        constraints,
        boundingUnresolved,
        protectedUnresolved,
        intensityUnresolved,
    } = resolveDrawingConstraints(
        bounding,
        collision,
        threshold,
        structureSet,
        image
    );
    if (boundingUnresolved !== null) {
        console.warn(
            `Inside drawing constraint references ROI ${boundingUnresolved}, ` +
                `which is not in the active structure set; constraint is inactive.`
        );
    }
    if (protectedUnresolved > 0) {
        console.warn(
            `${protectedUnresolved} protected (Outside) drawing-constraint ROI(s) ` +
                `are not in the active structure set; they are inactive.`
        );
    }
    if (intensityUnresolved !== null) {
        console.warn(
            `Intensity-threshold drawing constraint is inactive: ${intensityUnresolved}.`
        );
    }
    return constraints;
}
